import { faCode } from "@fortawesome/pro-solid-svg-icons";
import Accordion from "@intility/bifrost-react/Accordion";
import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Inline from "@intility/bifrost-react/Inline";
import Message from "@intility/bifrost-react/Message";
import Section from "@intility/bifrost-react/Section";
import Table from "@intility/bifrost-react/Table";
import { transformer } from "@openfga/syntax-transformer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import type { AuthorizationModel } from "~/api/fga-types";
import {
  getActiveManagementModelId,
  listManagementModels,
  writeManagementModel,
} from "~/api/management";
import { useCapabilities } from "~/auth/capabilities";
import { PageLayout } from "~/components/layout/PageLayout";
import ModelDetails from "~/components/model/ModelDetails";

const EXAMPLE_DSL = `model
  schema 1.1

type user

type document
  relations
    define viewer: [user]
    define editor: [user]`;

function modelToDsl(model: AuthorizationModel): string | null {
  try {
    return transformer.transformJSONToDSL({
      schema_version: model.schemaVersion,
      type_definitions: model.typeDefinitions,
    } as Parameters<typeof transformer.transformJSONToDSL>[0]);
  } catch {
    return null;
  }
}

export default function ManagementAuthorizationModels() {
  const { can } = useCapabilities();
  const canWrite = can("governance.write");
  const [models, setModels] = useState<AuthorizationModel[]>([]);
  const [activeModelId, setActiveModelId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dsl, setDsl] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const loadModels = useCallback(() => {
    setLoading(true);
    Promise.all([listManagementModels(), getActiveManagementModelId()])
      .then(([modelsRes, pinnedId]) => {
        const list = modelsRes.authorizationModels;
        setModels(list);
        setActiveModelId(pinnedId);
        const active = pinnedId ? list.find((m) => m.id === pinnedId) : list[0];
        setDsl(active ? (modelToDsl(active) ?? "") : EXAMPLE_DSL);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const active = activeModelId
    ? (models.find((m) => m.id === activeModelId) ?? null)
    : (models[0] ?? null);
  const currentDsl = useMemo(
    () => (active ? modelToDsl(active) : null),
    [active],
  );
  const dirty = dsl.trim() !== (currentDsl ?? "").trim();

  const handleSave = () => {
    if (!dsl.trim()) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    let modelJson: string;
    try {
      modelJson = transformer.transformDSLToJSON(dsl.trim());
    } catch (err) {
      setSaveError(
        `Invalid FGA schema: ${err instanceof Error ? err.message : String(err)}`,
      );
      setSaving(false);
      return;
    }

    writeManagementModel(modelJson)
      .then((res) => {
        setSaveSuccess(
          `New model version written: ${res.authorizationModelId}`,
        );
        setEditing(false);
        loadModels();
      })
      .catch((err) => setSaveError(err.message))
      .finally(() => setSaving(false));
  };

  const handleCancel = () => {
    setDsl(currentDsl ?? EXAMPLE_DSL);
    setSaveError(null);
    setEditing(false);
  };

  if (loading) return <p className="bf-p">Loading model...</p>;
  if (error) return <p className="bf-p bfc-alert">Error: {error}</p>;

  return (
    <PageLayout
      title="Management Authorization Model"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Management" },
        { label: "Model" },
      ]}
      description="The type-and-relation schema for the management store (access-over-access for Keycloak and OpenFGA modules). Saving writes a new model version; the active version is pinned by the OpenFga__AuthorizationModelId env var in argo/apps/fga-management-api."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {saveSuccess && (
          <Message state="success" header="Model written">
            {saveSuccess}
          </Message>
        )}

        {active ? (
          <>
            <p className="bf-p">
              Active model: <code>{active.id}</code> &middot; schema{" "}
              {active.schemaVersion} &middot;{" "}
              {active.typeDefinitions?.length ?? 0} types
            </p>
            <ModelDetails typeDefinitions={active.typeDefinitions ?? []} />
          </>
        ) : (
          <Message state="warning" header="No model yet">
            This store has no authorization model. Expand the schema below to
            write the first one.
          </Message>
        )}

        <Accordion>
          <Accordion.Item
            icon={faCode}
            title={
              <>
                Active schema (FGA DSL){" "}
                {active ? (
                  <Badge state="neutral">schema {active.schemaVersion}</Badge>
                ) : (
                  <Badge state="warning">none yet</Badge>
                )}
              </>
            }
          >
            <textarea
              value={dsl}
              onChange={(e) => setDsl(e.target.value)}
              readOnly={!editing}
              placeholder={EXAMPLE_DSL}
              rows={24}
              spellCheck={false}
              style={{
                width: "100%",
                fontFamily: "monospace",
                fontSize: 13,
                lineHeight: 1.5,
                resize: "vertical",
                padding: 12,
                borderRadius: 4,
                border: "1px solid var(--bf-border-color)",
                backgroundColor: editing
                  ? "var(--bf-base-bg)"
                  : "var(--bfc-base-2)",
                color: "var(--bf-base-color)",
                tabSize: 2,
              }}
            />

            {saveError && (
              <Message
                state="alert"
                header="Could not save"
                style={{ marginTop: 12 }}
              >
                {saveError}
              </Message>
            )}

            {canWrite && (
              <Inline style={{ marginTop: 12 }}>
                {editing ? (
                  <>
                    <Button
                      variant="filled"
                      onClick={handleSave}
                      disabled={saving || !dirty || !dsl.trim()}
                    >
                      {saving ? "Saving..." : "Save model"}
                    </Button>
                    <Button
                      variant="flat"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="filled" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                )}
              </Inline>
            )}
          </Accordion.Item>
        </Accordion>

        <Section>
          <Section.Header>Version history</Section.Header>
          <Table noBorder>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Model ID</Table.HeaderCell>
                <Table.HeaderCell>Schema Version</Table.HeaderCell>
                <Table.HeaderCell>Types</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {models.map((model) => (
                <Table.Row key={model.id}>
                  <Table.Cell>
                    <Link
                      className="bf-link"
                      to={`/management/model/${model.id}`}
                    >
                      <code>{model.id}</code>
                    </Link>
                    {model.id === activeModelId && (
                      <>
                        {" "}
                        <Badge state="success" pill>
                          active
                        </Badge>
                      </>
                    )}
                  </Table.Cell>
                  <Table.Cell>{model.schemaVersion}</Table.Cell>
                  <Table.Cell>{model.typeDefinitions?.length ?? 0}</Table.Cell>
                </Table.Row>
              ))}
              {models.length === 0 && (
                <Table.Row>
                  <Table.Cell>No models found</Table.Cell>
                  <Table.Cell />
                  <Table.Cell />
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Section>
      </div>
    </PageLayout>
  );
}
